/**
 * When a stem rewrite turns a sentence completion into a focused question,
 * leftover choice fragments no longer fit. Harmonize keeps each letter.
 */

export type StemChoiceOption = {
  id: string
  text: string
}

const ELLIPSIS = /^(?:\u2026|\.{2,})\s*/
const CONTINUATION =
  /^(?:predominantly\s+)?(?:in|within|inside|across|at|on|via|by|through|from)\s+(?:the\s+)?/i
const CUT_CLAUSE = /\s+(?:producing|generating|via|requiring|using|that)\b[\s\S]*$/i

export function isCompleteQuestionStem(stem: string): boolean {
  return stem.trim().includes("?")
}

export function isSentenceCompletionChoice(text: string): boolean {
  const trimmed = text.trim()
  if (!trimmed) return false
  if (ELLIPSIS.test(trimmed)) return true
  if (/^(predominantly|within|inside|across)\b/i.test(trimmed)) return true
  return /^[a-z]/.test(trimmed) && CONTINUATION.test(trimmed)
}

export function choicesNeedStemHarmonize(
  stem: string,
  options: readonly StemChoiceOption[],
): boolean {
  if (!isCompleteQuestionStem(stem)) return false
  const filled = options.filter((option) => option.text.trim())
  if (filled.length < 2) return false
  return filled.filter((option) => isSentenceCompletionChoice(option.text)).length >= 2
}

export function harmonizeChoiceText(text: string): string {
  let next = text.trim().replace(ELLIPSIS, "").trim()
  next = next.replace(CONTINUATION, "").trim()
  next = next.replace(CUT_CLAUSE, "").trim()
  next = next.replace(/[.,;:]+$/, "").trim()
  if (!next) {
    next = text.trim().replace(ELLIPSIS, "").trim()
  }
  if (!next) return text.trim()
  return next.charAt(0).toUpperCase() + next.slice(1)
}

export function harmonizeChoicesToStem(
  options: readonly StemChoiceOption[],
): Array<{ id: string; text: string; from: string }> {
  return options
    .filter((option) => option.text.trim())
    .map((option) => ({
      id: option.id,
      text: isSentenceCompletionChoice(option.text)
        ? harmonizeChoiceText(option.text)
        : option.text.trim(),
      from: option.text.trim(),
    }))
    .filter((row) => row.text && row.text !== row.from)
}
